/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type ParsedUrlQuery } from 'querystring';
import { stringifyUrl, parseUrl } from '../utils/urlHelpers';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import type { FilterSelectionType, HandleFilterType } from '../types/filterTypes';
import { useFacetedUrlEnabled } from './useFacetedUrlEnabled';
import { useGetFilterAnalytics } from './useGetFilterAnalytics';
import { sessionStorage } from '@/utils/sessionStorage';
import { useRouter } from 'next/navigation';

export const useFilterAndRedirect = (): ((
  filter: HandleFilterType,
  onFilterSelect?: (a: HandleFilterType) => void,
) => void) => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const facetedUrlEnabled = useFacetedUrlEnabled();
  const getFilterAnalytics = useGetFilterAnalytics();

  const handleFilterAndRedirect = (
    filter: HandleFilterType,
    onFilterSelect?: (a: HandleFilterType) => void,
  ) => {
    const { query } = legacyRouter;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { brandName, loadMore, ...currentQuery } = query;
    const selectedFilterType: FilterSelectionType = {
      type: filter.selectionType,
      facet: filter.displayName,
      value: filter.refinement.label,
      isStandardFilter: filter.isStandardFilter,
    };

    const filterAnalytics = getFilterAnalytics(selectedFilterType, Boolean(filter.checked));

    sessionStorage.setItem('filterSelectionAnalytics', filterAnalytics);
    if (!filterAnalytics.length) {
      sessionStorage.removeItem('filterSelectionAnalytics');
      sessionStorage.removeItem('quickFilters');
    }

    if (onFilterSelect) {
      onFilterSelect(filter);
    }

    if (brandName) {
      void router.push(getBrandShelfUrl(filter, query, legacyRouter.asPath));
    } else {
      void router.push(
        facetedUrlEnabled
          ? getFacetedUrl(filter, currentQuery)
          : getNonFacetedUrl(filter, currentQuery),
      );
    }
  };

  return handleFilterAndRedirect;
};

const getBrandShelfUrl = (filter: HandleFilterType, query: ParsedUrlQuery, asPath: string) => {
  const { brandName, filters, ...currentQuery } = query;
  const { checked, refinement } = filter;
  const { properties } = refinement ?? {};

  const { filters: selectedFilter } = properties ?? {};
  const { query: seoUrlQuery } = parseUrl(properties.seoUrl ?? '');
  let filtersArr = (filters as string | undefined)?.split('-') ?? [];

  if (selectedFilter || filtersArr.length) {
    if (checked) {
      filtersArr.push(selectedFilter);
    } else {
      filtersArr = filtersArr.filter((currentFilter) => currentFilter !== selectedFilter);
    }
  }

  // Extract the current path prefix (/brands or /marcas) from asPath
  const pathPrefix = asPath.split('/')[1]; // Extract 'brands' or 'marcas'

  return stringifyUrl(
    {
      url: `/${pathPrefix}/${brandName as string}`,
      query: {
        ...currentQuery,
        facet: null,
        ...seoUrlQuery,
        filters: filtersArr.length ? filtersArr.join('-') : null,
        loadMore: null,
        pageNumber: null,
        currentPage: null,
      },
    },
    {
      skipNull: true,
    },
  );
};

const getFacetedUrl = (
  filter: HandleFilterType,
  currentQuery: { [p: string]: string | string[] | undefined },
): string => {
  const { checked, refinement, dimensionName } = filter;
  const { unCheckedOptimizedUrl, optimizedSeoUrl, seoUrl } = refinement.properties;
  const { query } = parseUrl(seoUrl);
  if (checked === false && currentQuery.facet && !query.facet) {
    delete currentQuery.facet;
  }
  return stringifyUrl(
    {
      url: parseUrl(checked ? optimizedSeoUrl : unCheckedOptimizedUrl).url,
      query: {
        ...currentQuery,
        facet: null,
        ...query,
        cmsPath: null,
        slug: null,
        seourl: null,
        seoUrlPath: null,
        filters: null,
        pageNumber: null,
        currentPage: null,
        ...(!!dimensionName &&
          !checked &&
          (dimensionName.includes('vehicle_fitment') ||
            refinement?.properties?.internalName?.toUpperCase().includes('VEHICLE_SPECIFIC')) && {
            ignoreVehicleSpecificProductsCheck: true,
          }),
        ...(checked &&
          !!dimensionName &&
          (dimensionName.includes('vehicle_fitment') ||
            refinement?.properties?.internalName?.toUpperCase().includes('VEHICLE_SPECIFIC')) && {
            ignoreVehicleSpecificProductsCheck: false,
          }),
      },
    },
    {
      skipNull: true,
    },
  );
};

const getNonFacetedUrl = (
  filter: HandleFilterType,
  currentQuery: { [p: string]: string | string[] | undefined },
): string => {
  let filteredUrl;
  const { checked, refinement, dimensionName } = filter;
  const { properties } = refinement ?? {};
  const { filters: filterId, seoUrl } = properties ?? {};
  const { query, url } = parseUrl(seoUrl ?? '');

  if (checked === false) {
    // remove filterId with any attached '-'s
    const filters = String(query.filters);
    query.filters = filters
      .replace(`-${filterId}`, '')
      .replace(`${filterId}-`, '')
      .replace(filterId, '');
    if (!query.facet) {
      delete currentQuery.facet;
    }
    if (query.filters === '' || !filterId) {
      delete query.filters;
      delete currentQuery.filters;
    }
    const newQuery = {
      ...currentQuery,
      facet: null,
      ...query,
      cmsPath: null,
      slug: null,
      pageNumber: null,
      currentPage: null,
      seoUrlPath: null,
      ...(!!dimensionName &&
        (dimensionName.includes('vehicle_fitment') ||
          refinement?.properties?.internalName?.toUpperCase().includes('VEHICLE_SPECIFIC')) && {
          ignoreVehicleSpecificProductsCheck: true,
        }),
    };

    filteredUrl = stringifyUrl(
      {
        url,
        query: newQuery,
      },
      {
        skipNull: true,
      },
    );
  } else {
    const newQuery = {
      ...currentQuery,
      facet: null,
      ...query,
      cmsPath: null,
      slug: null,
      pageNumber: null,
      currentPage: null,
      ...(!!dimensionName &&
        (dimensionName.includes('vehicle_fitment') ||
          refinement.label.toUpperCase().includes('VEHICLE_SPECIFIC')) && {
          ignoreVehicleSpecificProductsCheck: false,
        }),
    };

    filteredUrl = stringifyUrl(
      {
        url,
        query: newQuery,
      },
      {
        skipNull: true,
      },
    );
  }

  return filteredUrl;
};
